import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useAppSelector } from '@/store/hooks'
import Button from '@/components/ui/Button'
import DataGrid from '@/components/data/DataGrid'
import { designTokens } from '@/utils/design-tokens'
import MemberSelect from '@/components/ui/MemberSelect'
import WorkspaceSelect from '@/components/ui/WorkspaceSelect'
import DateRangePicker from '@/components/ui/DateRangePicker'
import {
  getInitialDateRange,
  formatDateForAPI,
  loadDateRangeFromStorage,
  parseDateFromStorage
} from '@/components/ui/DateRangePicker/utils'
import { HistoryActionService } from '@/api/generated/services/HistoryActionService'
import type { ColDef } from 'ag-grid-community'
import ActionTypeSelect from './components/ActionTypeSelect'
import ActionModelSelect from './components/ActionModelSelect'
import { AG_GRID_LOCALE_KR } from '@/locales/ag-grid-ko-KR'
import {
  type ActionHistoryItem,
  type FlatChangeRow,
  buildChangeColumnDefs,
  changeRangeCopyFormatter,
  flattenChangeRecords,
  useChangeValueFormatter,
  useUuidNameMaps
} from './changeHistoryTable'

const ChangeHistoryPage: React.FC = () => {
  const currentWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)

  // Filter state
  const [selectedMemberId, setSelectedMemberId] = useState('')
  const [selectedMemberName, setSelectedMemberName] = useState('')
  const [, setSelectedMemberEmail] = useState('') // 값은 아직 화면에서 쓰지 않는다
  // 워크스페이스 필터는 현재 선택된 워크스페이스를 기본값으로 사용
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(() => currentWorkspace?.id || '')
  const [selectedWorkspaceName, setSelectedWorkspaceName] = useState(() => currentWorkspace?.name || '')
  const [selectedActionType, setSelectedActionType] = useState('')
  const [selectedActionTypeName, setSelectedActionTypeName] = useState('')
  const [selectedActionModel, setSelectedActionModel] = useState('')
  const [selectedActionModelName, setSelectedActionModelName] = useState('')

  // Date range state - initialize from localStorage
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const [initialStart] = getInitialDateRange()
    return initialStart
  })
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const [, initialEnd] = getInitialDateRange()
    return initialEnd
  })

  // State to trigger re-render when localStorage changes
  const [storageUpdateTrigger, setStorageUpdateTrigger] = useState(0)

  // Get display dates from localStorage (for DateRangePicker display)
  const displayDates = useMemo((): [Date | null, Date | null] => {
    try {
      const stored = loadDateRangeFromStorage()
      return [
        parseDateFromStorage(stored.start_date),
        parseDateFromStorage(stored.end_date)
      ]
    } catch {
      const [initialStart, initialEnd] = getInitialDateRange()
      return [initialStart, initialEnd]
    }
  }, [storageUpdateTrigger])

  // UUID → 표시명 매핑 (변경 전/후 값 치환용)
  const nameMaps = useUuidNameMaps(currentWorkspace?.org_id)

  // Accumulated record data (누적 로딩)
  const [records, setRecords] = useState<ActionHistoryItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Pagination state (서버는 '기록 단위' 페이지네이션, 화면은 누적)
  const [loadedPage, setLoadedPage] = useState(0)
  const [totalItems, setTotalItems] = useState(0)
  const perPage = 50 // 한 번에 불러올 기록 수 (fetch batch)

  // Fetch a page of records. targetPage === 1 이면 초기화(reset), 그 외에는 누적(append)
  const fetchPage = useCallback(async (targetPage: number) => {
    const isReset = targetPage === 1
    if (isReset) {
      setIsLoading(true)
    } else {
      setIsLoadingMore(true)
    }
    setError(null)

    try {
      // Build API parameters object
      const apiParams: {
        orgId?: string
        memberId?: string
        workspaceId?: string
        actionType?: string
        actionTargetModel?: string
        startDate?: string
        endDate?: string
        page: number
        perPage: number
      } = {
        page: targetPage,
        perPage,
        orgId: currentWorkspace?.org_id || undefined,
        memberId: selectedMemberId || undefined,
        workspaceId: selectedWorkspaceId || undefined,
        actionType: selectedActionType || undefined,
        actionTargetModel: selectedActionModel || undefined
      }

      // Add date parameters if both dates are set
      if (startDate && endDate) {
        apiParams.startDate = formatDateForAPI(startDate)
        apiParams.endDate = formatDateForAPI(endDate)
      }

      const response = await HistoryActionService.appsHistoryViewsListActionHistories(apiParams)

      if (response?.data && response.meta) {
        const newItems = response.data as ActionHistoryItem[]
        setTotalItems(response.meta.total || 0)
        setRecords(prev => (isReset ? newItems : [...prev, ...newItems]))
        setLoadedPage(targetPage)
      } else if (isReset) {
        setRecords([])
        setTotalItems(0)
        setLoadedPage(1)
      }
    } catch (err) {
      console.error('Failed to fetch history data:', err)
      setError('변경 이력을 불러오는데 실패했습니다.')
    } finally {
      setIsLoading(false)
      setIsLoadingMore(false)
    }
  }, [currentWorkspace, selectedMemberId, selectedWorkspaceId, selectedActionType, selectedActionModel, startDate, endDate, perPage])

  // 다음 기록 페이지를 추가로 불러오기 (더 보기)
  const loadMore = useCallback(() => {
    if (isLoading || isLoadingMore) return
    if (records.length >= totalItems) return
    fetchPage(loadedPage + 1)
  }, [isLoading, isLoadingMore, records.length, totalItems, loadedPage, fetchPage])

  // 전역 워크스페이스 전환 시 필터도 해당 워크스페이스로 동기화
  useEffect(() => {
    setSelectedWorkspaceId(currentWorkspace?.id || '')
    setSelectedWorkspaceName(currentWorkspace?.name || '')
  }, [currentWorkspace?.id, currentWorkspace?.name])

  // Load history data on component mount and when filters change
  useEffect(() => {
    fetchPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMemberId, selectedWorkspaceId, selectedActionType, selectedActionModel, startDate, endDate, perPage])

  // 누적된 기록을 '변경 항목당 한 행'으로 평탄화
  const flattenedRows = useMemo<FlatChangeRow[]>(() => flattenChangeRecords(records), [records])

  const formatChangeValue = useChangeValueFormatter(nameMaps)
  const columnDefs: ColDef[] = useMemo(() => buildChangeColumnDefs({ formatChangeValue }), [formatChangeValue])

  // Filter handlers
  const handleMemberSelect = useCallback((memberId: string, memberName: string, memberEmail: string) => {
    setSelectedMemberId(memberId)
    setSelectedMemberName(memberName)
    setSelectedMemberEmail(memberEmail)
  }, [])

  const handleWorkspaceSelect = useCallback((workspaceId: string, workspaceName: string) => {
    setSelectedWorkspaceId(workspaceId)
    setSelectedWorkspaceName(workspaceName)
  }, [])

  const handleActionTypeSelect = useCallback((actionType: string, actionTypeName: string) => {
    setSelectedActionType(actionType)
    setSelectedActionTypeName(actionTypeName)
  }, [])

  const handleActionModelSelect = useCallback((actionModel: string, actionModelName: string) => {
    setSelectedActionModel(actionModel)
    setSelectedActionModelName(actionModelName)
  }, [])

  // Handle date range change from DateRangePicker
  const handleDateRangeChange = useCallback((newStartDate: Date | null, newEndDate: Date | null) => {
    if (newStartDate && newEndDate) {
      setStartDate(newStartDate)
      setEndDate(newEndDate)
      setStorageUpdateTrigger(prev => prev + 1)
    }
  }, [])

  // Clear all filters (워크스페이스는 기본값인 현재 워크스페이스로 복원)
  const clearAllFilters = useCallback(() => {
    setSelectedMemberId('')
    setSelectedMemberName('')
    setSelectedMemberEmail('')
    setSelectedWorkspaceId(currentWorkspace?.id || '')
    setSelectedWorkspaceName(currentWorkspace?.name || '')
    setSelectedActionType('')
    setSelectedActionTypeName('')
    setSelectedActionModel('')
    setSelectedActionModelName('')

    // Reset date range to initial values
    const [initialStart, initialEnd] = getInitialDateRange()
    setStartDate(initialStart)
    setEndDate(initialEnd)
    setStorageUpdateTrigger(prev => prev + 1)
  }, [currentWorkspace?.id, currentWorkspace?.name])

  // Check if any filter is active (워크스페이스는 현재 워크스페이스와 다를 때만 활성으로 판정)
  const hasActiveFilters = useMemo(() => {
    return (
      (selectedMemberId && selectedMemberName !== '전체') ||
      selectedWorkspaceId !== (currentWorkspace?.id || '') ||
      (selectedActionType && selectedActionTypeName !== '전체') ||
      (selectedActionModel && selectedActionModelName !== '전체')
    )
  }, [selectedMemberId, selectedMemberName, selectedWorkspaceId, selectedActionType, selectedActionTypeName, selectedActionModel, selectedActionModelName, currentWorkspace?.id])

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Filter & Action Bar - Single Row */}
      <div
        className="py-2 px-4 flex-shrink-0 flex items-center gap-2 flex-wrap justify-end rounded-lg"
        style={{
          backgroundColor: designTokens.colors.gray[0]
        }}
      >
        {/* Date Range Selection */}
        <div className="w-[230px] flex-shrink-0">
          <DateRangePicker
            startDate={displayDates[0]}
            endDate={displayDates[1]}
            onDateRangeChange={handleDateRangeChange}
            timePeriod="period"
            onTimePeriodChange={() => {}}
            triggerMode="icon"
            showDateText={true}
            workspaceId={currentWorkspace?.id}
          />
        </div>

        {/* Member Selection */}
        <div className="w-[200px] flex-shrink-0">
          <MemberSelect
            workspaceId={currentWorkspace?.id || ''}
            selectedMemberId={selectedMemberId}
            selectedMemberName={selectedMemberName || '[전체] 사용자 선택'}
            onSelect={handleMemberSelect}
            placeholder="[전체] 사용자 선택"
            label="사용자"
            showAllOption={true}
          />
        </div>

        {/* Workspace Selection */}
        <div className="w-[200px] flex-shrink-0">
          <WorkspaceSelect
            selectedWorkspaceId={selectedWorkspaceId}
            selectedWorkspaceName={selectedWorkspaceName || '[전체] 워크스페이스'}
            onSelect={handleWorkspaceSelect}
            placeholder="[전체] 워크스페이스"
            showAllOption={true}
          />
        </div>

        {/* Action Type Selection */}
        <div className="w-[200px] flex-shrink-0">
          <ActionTypeSelect
            selectedActionType={selectedActionType}
            selectedActionTypeName={selectedActionTypeName || '[전체] 액션 종류'}
            onSelect={handleActionTypeSelect}
            placeholder="[전체] 액션 종류"
            showAllOption={true}
          />
        </div>

        {/* Action Model (변경 대상) Selection */}
        <div className="w-[200px] flex-shrink-0">
          <ActionModelSelect
            selectedActionModel={selectedActionModel}
            selectedActionModelName={selectedActionModelName || '[전체] 변경 대상'}
            onSelect={handleActionModelSelect}
            placeholder="[전체] 변경 대상"
            showAllOption={true}
          />
        </div>

        {/* Clear Filters Button - conditional */}
        {hasActiveFilters && (
          <Button
            size="medium"
            color="white"
            onClick={clearAllFilters}
            className="h-10 px-[10px] flex items-center gap-1 flex-shrink-0 whitespace-nowrap"
          >
            필터 초기화
          </Button>
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-h-0 mt-6">
        <div className="bg-white rounded-lg shadow-sm border" style={{ borderColor: designTokens.colors.gray[1] }}>
          {error && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400 m-4">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <DataGrid
            columnDefs={columnDefs}
            rowData={flattenedRows}
            loading={isLoading}
            className="ag-theme-alpine"
            headerHeight={40}
            rowHeight={42}
            pagination={false}
            height="calc(100vh - 258px)"
            localeText={AG_GRID_LOCALE_KR}
            // Row ID (flattened: 기록ID__항목index)
            getRowId={(params: any) => {
              if (!params.data) return null
              return params.data.id
            }}
            // 범위 선택 기능 활성화
            enableRangeSelection={true}
            enableCellCopy={true}
            suppressRowClickSelection={true}
            rangeCopyFormatter={changeRangeCopyFormatter}
          />

          {/* Footer: 누적 로딩 (더 보기) */}
          <div
            className="flex items-center justify-center gap-4 py-3 border-t"
            style={{ borderTopColor: designTokens.colors.gray[1] }}
          >
            <span
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '14px',
                color: designTokens.colors.gray[5]
              }}
            >
              전체 {totalItems.toLocaleString()}건 중 {records.length.toLocaleString()}건 로드
              {' · '}변경 항목 {flattenedRows.length.toLocaleString()}개
            </span>
            {records.length < totalItems && (
              <Button
                size="small"
                color="white"
                onClick={loadMore}
                disabled={isLoadingMore}
                className="flex-shrink-0"
              >
                {isLoadingMore ? '불러오는 중...' : '더 보기'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChangeHistoryPage
